import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Avoid circular dependency by defining a subset of the state type here
interface RootStateSubset {
    auth: {
        accessToken: string | null;
        refreshToken: string | null;
    };
}

// Create a base query instance for CMS API calls
const baseQuery = fetchBaseQuery({
    baseUrl: import.meta.env.VITE_CMS_API_BASE_URL || 'https://signage-api-dev.wejha.com',
    prepareHeaders: (headers, { getState }) => {
        // Auto header injection
        const token = (getState() as unknown as RootStateSubset).auth.accessToken;
        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
        headers.set('accept', 'application/json');
        return headers;
    },
});

/**
 * Custom base query for CMS that leverages the main API's refresh mechanism.
 */
const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, apiInstance, extraOptions) => {
    let result = await baseQuery(args, apiInstance, extraOptions);

    if (result.error && result.error.status === 401) {
        console.log('CMS Token expired, attempting refresh...');

        const state = apiInstance.getState() as RootStateSubset;
        const refreshToken = state.auth.refreshToken;

        if (refreshToken) {
            const mainApiBaseUrl = (import.meta.env.VITE_API_BASE_URL as string) || 'https://contentcreator-api-dev.wejha.com/api/v1';
            
            try {
                const response = await fetch(`${mainApiBaseUrl}/auth/refresh-tokens`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken }),
                });

                if (response.ok) {
                    const refreshData = await response.json();
                    console.log('CMS: Token refreshed successfully, retrying request...');
                    const data = refreshData.data || refreshData;

                    // Break circular dependency: Use direct dispatch with action types
                    apiInstance.dispatch({
                        type: 'auth/setCredentials',
                        payload: {
                            user: data.user,
                            accessToken: data.tokens.access.token,
                            refreshToken: data.tokens.refresh.token,
                        }
                    });

                    // Retry the initial CMS query
                    result = await baseQuery(args, apiInstance, extraOptions);
                } else {
                    apiInstance.dispatch({ type: 'auth/logout' });
                }
            } catch (err) {
                apiInstance.dispatch({ type: 'auth/logout' });
            }
        } else {
            apiInstance.dispatch({ type: 'auth/logout' });
        }
    }
    return result;
};

// Dedicated CMS API slice
export const cmsApi = createApi({
    reducerPath: 'cmsApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Assets', 'Webhooks'],
    endpoints: () => ({}),
});
